import { ChevronDown, ChevronRight, Download, Hash, LogOut, Plus, Settings, Star } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { colorHex } from "@todoer/shared";
import { openNewProject } from "../components/Layout.js";
import { NAV_FOOTER, NAV_PRIMARY, NAV_TOOLS, NavIcon, NotificationsButton, SyncStatus, useNavData, type NavDef } from "../components/nav.js";
import { Avatar, cx, IconButton, PageHeader } from "../components/ui.js";
import { signOut } from "../lib/auth.js";
import { clearLocalData, useUser } from "../lib/db.js";
import { isStandalone, startInstall } from "../lib/install.js";

function Row({ to, icon, label, count, color }: { to: string; icon: ReactNode; label: string; count?: number; color?: string }) {
  return (
    <li>
      <Link to={to} className="flex min-h-12 items-center gap-3 px-4 text-body text-text active:bg-pressed md:hover:bg-hover">
        <span className="grid w-6 place-items-center text-text-muted" style={color ? { color } : undefined}>{icon}</span>
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {!!count && <span className="tabular text-meta text-text-faint">{count}</span>}
        <ChevronRight size={16} className="text-text-faint" aria-hidden />
      </Link>
    </li>
  );
}

function Group({ title, children, action }: { title?: string; children: ReactNode; action?: ReactNode }) {
  return (
    <section className="mb-6">
      {(title || action) && (
        <div className="mb-1.5 flex items-center px-1">
          {title && <h2 className="text-caption font-semibold uppercase text-text-faint">{title}</h2>}
          {action && <span className="ml-auto">{action}</span>}
        </div>
      )}
      <ul className="divide-y divide-border overflow-hidden rounded-card border border-border bg-surface-raised">{children}</ul>
    </section>
  );
}

/** Phone navigation: everything the desktop sidebar holds, as a page behind the Browse tab. */
export function Browse() {
  const user = useUser();
  const nav = useNavData();
  const [projectsOpen, setProjectsOpen] = useState(true);
  const navRow = (d: NavDef) => <Row key={d.to} to={d.to} icon={<NavIcon icon={d.icon} size={20} />} label={d.label} count={d.countKey ? nav.counts[d.countKey] : undefined} />;
  const logout = async () => { await signOut(); await clearLocalData(); window.location.href = "/login"; };

  return (
    <div>
      <PageHeader title="Browse" actions={<><NotificationsButton /><Link to="/settings" aria-label="Settings" className="grid h-11 w-11 place-items-center rounded-control text-text-muted hover:bg-hover md:h-8 md:w-8"><Settings size={20} aria-hidden /></Link></>} />
      <div className="mb-6 flex items-center gap-3 rounded-card border border-border bg-surface-raised p-4">
        <Avatar person={user} size={40} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-heading font-semibold">{user?.name}</div>
          <div className="truncate text-meta text-text-faint">{user?.email}</div>
        </div>
        <SyncStatus />
      </div>

      <Group>{NAV_PRIMARY.filter((d) => d.to !== "/today" && d.to !== "/upcoming").map(navRow)}</Group>
      <Group title="Tools">{NAV_TOOLS.map(navRow)}</Group>
      {(nav.favProjects.length > 0 || nav.favFilters.length > 0 || nav.favLabels.length > 0) && (
        <Group title="Favourites">
          {nav.favProjects.map((p) => <Row key={p.id} to={`/project/${p.id}`} icon={<Hash size={20} aria-hidden />} color={colorHex(p.color)} label={p.name} count={nav.counts.byProject.get(p.id)} />)}
          {nav.favFilters.map((f) => <Row key={f.id} to={`/filter/${f.id}`} icon={<Star size={20} aria-hidden />} color={colorHex(f.color)} label={f.name} />)}
          {nav.favLabels.map((l) => <Row key={l.id} to={`/label/${l.id}`} icon={<span className="text-heading font-semibold">@</span>} color={colorHex(l.color)} label={l.name} />)}
        </Group>
      )}
      <section className="mb-6">
        <div className="mb-1.5 flex items-center px-1">
          <button type="button" onClick={() => setProjectsOpen((o) => !o)} aria-expanded={projectsOpen} className="flex min-h-11 items-center gap-1 text-caption font-semibold uppercase text-text-faint">
            My Projects {projectsOpen ? <ChevronDown size={14} aria-hidden /> : <ChevronRight size={14} aria-hidden />}
          </button>
          <IconButton icon={Plus} label="Add project" className="ml-auto" onClick={() => openNewProject(null)} />
        </div>
        {projectsOpen && (
          <ul className="divide-y divide-border overflow-hidden rounded-card border border-border bg-surface-raised">
            {nav.tree.map((p) => (
              <li key={p.id}>
                <Link to={`/project/${p.id}`} className="flex min-h-12 items-center gap-3 pr-4 text-body active:bg-pressed md:hover:bg-hover" style={{ paddingLeft: 16 + p.depth * 20 }}>
                  <Hash size={20} aria-hidden style={{ color: colorHex(p.color) }} />
                  <span className="min-w-0 flex-1 truncate">{p.name}</span>
                  {!!nav.counts.byProject.get(p.id) && <span className="tabular text-meta text-text-faint">{nav.counts.byProject.get(p.id)}</span>}
                  <ChevronRight size={16} className="text-text-faint" aria-hidden />
                </Link>
              </li>
            ))}
            {nav.loaded && !nav.tree.length && (
              <li><button type="button" onClick={() => openNewProject(null)} className="flex min-h-12 w-full items-center gap-3 px-4 text-body text-text-muted"><Plus size={20} aria-hidden />Add your first project</button></li>
            )}
          </ul>
        )}
      </section>
      <Group title="More">
        {NAV_FOOTER.map(navRow)}
        {!isStandalone() && (
          <li>
            <button type="button" onClick={() => void startInstall()} className="flex min-h-12 w-full items-center gap-3 px-4 text-left text-body text-text active:bg-pressed desk:hover:bg-hover">
              <span className="grid w-6 place-items-center text-brand-text"><Download size={20} aria-hidden /></span>
              <span className="min-w-0 flex-1">Install the app</span>
              <ChevronRight size={16} className="text-text-faint" aria-hidden />
            </button>
          </li>
        )}
      </Group>
      <button type="button" onClick={() => void logout()}
        className={cx("mb-4 flex min-h-12 w-full items-center justify-center gap-2 rounded-card border border-border bg-surface-raised text-body font-medium text-danger active:bg-pressed")}>
        <LogOut size={18} aria-hidden />Sign out
      </button>
    </div>
  );
}
